/**
 * Global command palette — the Frappe AwesomeBar analog.
 *
 * One box that both *navigates / creates* (static command index, client-side)
 * and *finds records* (server-side /api/v1/search, company-scoped + RBAC).
 * Opens on Cmd/Ctrl-K or by clicking the top-bar search box.
 *
 * Hand-rolled (no cmdk dependency) to match the project's dependency-light
 * `ui/dialog.tsx`. Keyboard: ↑/↓ move, ↵ open, Esc close; mouse hover also
 * sets the active row.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  Banknote,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CheckSquare,
  CircleUser,
  ClipboardList,
  Clock,
  Cog,
  CornerDownLeft,
  FileCheck2,
  FileText,
  GitBranch,
  Goal,
  Hash,
  LayoutDashboard,
  type LucideIcon,
  Receipt,
  Scale,
  ScanFace,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  TrendingUp,
  UserCheck,
  UserPlus,
  UserSearch,
  Users,
} from "lucide-react";

import {
  ALL_COMMANDS,
  type AppCommand,
  scoreCommand,
} from "@/lib/command-index";
import { useMe } from "@/lib/me";
import {
  type RecentEntry,
  type SearchHit,
  getRecent,
  pushRecent,
  useDebouncedValue,
  useRecordSearch,
} from "@/lib/use-global-search";
import { cn } from "@/lib/utils";

const ICONS: Record<string, LucideIcon> = {
  BadgeCheck, Banknote, BarChart3, Bell, BookOpen, Boxes, Briefcase, Building2,
  Calendar, CalendarClock, CalendarDays, CalendarPlus, CheckSquare, CircleUser,
  ClipboardList, Clock, Cog, FileCheck2, FileText, GitBranch, Goal, Hash,
  LayoutDashboard, Receipt, Scale, ScanFace, ScrollText, Settings, ShieldCheck,
  SlidersHorizontal, Smartphone, TrendingUp, UserCheck, UserPlus, UserSearch,
  Users,
};

function Icon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? FileText;
  return <Cmp size={16} strokeWidth={1.75} className={className} aria-hidden="true" />;
}

/** A single selectable row, flattened across all sections for keyboard nav. */
interface FlatItem {
  key: string;
  title: string;
  subtitle?: string;
  icon: string;
  route: string;
  /** What to persist to "recent" when chosen. */
  recent: RecentEntry;
}

interface Section {
  heading: string;
  items: FlatItem[];
}

const STATIC_GROUP_ORDER = ["Create", "Navigate", "Settings"] as const;
const MAX_STATIC = 12;

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const enabledModules = useMemo(() => me?.enabled_modules ?? [], [me]);

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const debounced = useDebouncedValue(query, 200);
  const recordSearch = useRecordSearch(debounced);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset state each time the palette opens; focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // Defer focus to after the dialog paints.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Module-aware static commands.
  const availableCommands = useMemo(
    () =>
      ALL_COMMANDS.filter(
        (c) => !c.requiresModule || enabledModules.includes(c.requiresModule),
      ),
    [enabledModules],
  );

  const trimmed = query.trim();
  const isEmpty = trimmed === "";

  // Build the sections (recent → static → records).
  const sections = useMemo<Section[]>(() => {
    const out: Section[] = [];

    if (isEmpty) {
      const recent = getRecent();
      if (recent.length) {
        out.push({
          heading: "Recent",
          items: recent.map((r) => ({
            key: `recent:${r.key}`,
            title: r.title,
            subtitle: r.subtitle,
            icon: r.icon,
            route: r.route,
            recent: r,
          })),
        });
      }
      // Empty state doubles as a navigation menu.
      const nav = availableCommands.filter((c) => c.group === "Navigate");
      out.push({ heading: "Jump to", items: nav.map(commandToItem) });
      return out;
    }

    // Static command matches, scored + grouped.
    const scored = availableCommands
      .map((c) => ({ c, s: scoreCommand(c, trimmed) }))
      .filter((x): x is { c: AppCommand; s: number } => x.s !== null)
      .sort((a, b) => b.s - a.s)
      .slice(0, MAX_STATIC);

    for (const group of STATIC_GROUP_ORDER) {
      const items = scored
        .filter((x) => x.c.group === group)
        .map((x) => commandToItem(x.c));
      if (items.length) out.push({ heading: group, items });
    }

    // Server-side record hits, grouped by label preserving relevance order.
    const hits = recordSearch.data?.results ?? [];
    const seen = new Set<string>();
    const order: string[] = [];
    const byLabel = new Map<string, FlatItem[]>();
    for (const h of hits) {
      if (!seen.has(h.label)) {
        seen.add(h.label);
        order.push(h.label);
        byLabel.set(h.label, []);
      }
      byLabel.get(h.label)!.push(hitToItem(h));
    }
    for (const label of order) {
      out.push({ heading: label, items: byLabel.get(label)! });
    }
    return out;
  }, [isEmpty, trimmed, availableCommands, recordSearch.data]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // Keep the active index in range as the list changes.
  useEffect(() => {
    setActive((i) => (flat.length === 0 ? 0 : Math.min(i, flat.length - 1)));
  }, [flat.length]);

  // Scroll the active row into view.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${active}"]`,
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const select = useCallback(
    (item: FlatItem | undefined) => {
      if (!item) return;
      pushRecent(item.recent);
      onClose();
      navigate(item.route);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(flat[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  const showSearching =
    !isEmpty && recordSearch.isFetching && (recordSearch.data?.results.length ?? 0) === 0;
  const nothing = flat.length === 0 && !showSearching;

  // Running counter so each row gets a global index for keyboard nav.
  let runningIndex = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/40 px-4 pt-[12vh] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-slate-100 px-4">
          <Search size={18} strokeWidth={1.75} className="text-slate" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            type="text"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- palette is opened on demand
            autoFocus
            placeholder="Search people, records, pages — or type a command…"
            className="h-12 w-full bg-transparent text-sm text-navy placeholder:text-slate-400 focus:outline-none"
            aria-label="Search"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-list"
            aria-autocomplete="list"
          />
        </div>

        {/* Results */}
        <div
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          className="max-h-[55vh] overflow-y-auto py-2"
        >
          {sections.map((section) => (
            <div key={section.heading} className="mb-1">
              <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {section.heading}
              </p>
              <ul>
                {section.items.map((item) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  const isActive = index === active;
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        data-index={index}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => select(item)}
                        onMouseMove={() => setActive(index)}
                        className={cn(
                          "flex w-full items-center gap-3 px-4 py-2 text-left text-sm",
                          isActive ? "bg-blue/10 text-navy" : "text-slate-700 hover:bg-slate-soft",
                        )}
                      >
                        <span
                          className={cn(
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                            isActive ? "bg-blue text-white" : "bg-slate-100 text-slate",
                          )}
                        >
                          <Icon name={item.icon} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{item.title}</span>
                          {item.subtitle && (
                            <span className="block truncate text-xs text-slate-400">
                              {item.subtitle}
                            </span>
                          )}
                        </span>
                        {isActive && (
                          <CornerDownLeft
                            size={14}
                            strokeWidth={1.75}
                            className="shrink-0 text-slate-400"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {showSearching && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Searching…</p>
          )}
          {nothing && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">
              No matches for “{trimmed}”.
            </p>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 border-t border-slate-100 bg-bg px-4 py-2 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd> open
          </span>
          <span className="flex items-center gap-1">
            <Kbd>esc</Kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-500">
      {children}
    </kbd>
  );
}

function commandToItem(c: AppCommand): FlatItem {
  return {
    key: c.id,
    title: c.title,
    subtitle: c.group === "Create" ? "Create" : undefined,
    icon: c.icon,
    route: c.route,
    recent: {
      key: c.id,
      title: c.title,
      subtitle: c.group === "Create" ? "Create" : undefined,
      route: c.route,
      icon: c.icon,
    },
  };
}

function hitToItem(h: SearchHit): FlatItem {
  return {
    key: `hit:${h.type}:${h.id}`,
    title: h.title,
    subtitle: h.subtitle || h.label,
    icon: h.icon,
    route: h.route,
    recent: {
      key: `hit:${h.type}:${h.id}`,
      title: h.title,
      subtitle: h.subtitle || h.label,
      route: h.route,
      icon: h.icon,
    },
  };
}
